import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { DisasterRecovery, BackupManager, createBackup } from '../utils/backupUtils';
import { 
  checkProductionHealth, 
  testCloudflareR2, 
  testCloudflareWorker,
  getHTMLCSStoImageCredentials,
  updateHTMLCSStoImageCredentials,
  resetHTMLCSStoImageCredentials,
  testHTMLCSStoImageAPI,
  createTestHTMLFile,
  getCloudflareCredentials,
  updateCloudflareCredentials
} from '../utils/realDeploymentUtils';
import { 
  Shield, 
  Download, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Database, 
  Server, 
  Wifi, 
  HardDrive,
  RefreshCw,
  Settings,
  Activity,
  Globe,
  Zap,
  Copy,
  ExternalLink,
  TestTube,
  Key,
  Save,
  RotateCcw,
  Eye,
  EyeOff,
  FileText,
  Upload
} from 'lucide-react';
import { ServiceStatus } from '../types';

interface SystemHealthDashboardProps {
  clients: any[];
  users: any[];
}

export const SystemHealthDashboard: React.FC<SystemHealthDashboardProps> = ({
  clients,
  users
}) => {
  const { user, hasPermission } = useAuth();
  const { showSuccess, showError, showInfo } = useToast();
  const [healthStatus, setHealthStatus] = useState<any>(null);
  const [integrityCheck, setIntegrityCheck] = useState<any>(null);
  const [productionHealth, setProductionHealth] = useState<any>(null);
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [backupSchedule, setBackupSchedule] = useState<'6h' | '12h' | '24h'>('6h');
  const [lastBackup, setLastBackup] = useState<Date | null>(null);
  const [currentTab, setCurrentTab] = useState<'health' | 'setup' | 'api'>('health');
  const [isTestingServices, setIsTestingServices] = useState(false);
  
  // API Credentials Management State
  const [apiCredentials, setApiCredentials] = useState({ userId: '', apiKey: '' });
  const [isEditingCredentials, setIsEditingCredentials] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [isSavingCredentials, setIsSavingCredentials] = useState(false);
  const [isTestingApi, setIsTestingApi] = useState(false);

  // Cloudflare Credentials Management State
  const [cloudflareCredentials, setCloudflareCredentials] = useState({
    accountEmail: '',
    apiKey: '',
    zoneId: '',
    r2BucketName: 'dealercentive-banners',
    accountId: ''
  });
  const [isEditingCloudflare, setIsEditingCloudflare] = useState(false);
  const [showCloudflareKey, setShowCloudflareKey] = useState(false);
  const [isSavingCloudflare, setIsSavingCloudflare] = useState(false);

  const [serviceStatuses, setServiceStatuses] = useState<ServiceStatus[]>([]);

  useEffect(() => {
    performHealthCheck();
    performIntegrityCheck();
    checkProductionServices();
    loadApiCredentials();
    loadCloudflareCredentials();
    
    const lastBackupTime = localStorage.getItem('lastBackupTime');
    if (lastBackupTime) {
      setLastBackup(new Date(lastBackupTime));
    }
  }, []);

  const loadApiCredentials = () => {
    const credentials = getHTMLCSStoImageCredentials();
    setApiCredentials(credentials);
  };

  const loadCloudflareCredentials = () => {
    const credentials = getCloudflareCredentials();
    setCloudflareCredentials({
      ...cloudflareCredentials,
      ...credentials,
      apiKey: credentials.apiKey === '••••••••••••••••' ? '' : credentials.apiKey
    });
  };

  const performHealthCheck = () => {
    const health = DisasterRecovery.performHealthCheck();
    setHealthStatus(health);
  };

  const performIntegrityCheck = () => {
    const integrity = DisasterRecovery.validateSystemIntegrity(clients, users);
    setIntegrityCheck(integrity);
  };

  const checkProductionServices = async () => {
    setIsTestingServices(true);
    try {
      const statuses = await checkProductionHealth();
      setServiceStatuses(statuses);
      
      // Update production health status based on service statuses
      const allOperational = statuses.every(service => service.status === 'operational');
      const anyDown = statuses.some(service => service.status === 'down');
      
      setProductionHealth({
        status: anyDown ? 'critical' : allOperational ? 'healthy' : 'degraded',
        services: statuses,
        lastChecked: new Date()
      });
    } catch (error) {
      showError('Service Check Failed', error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsTestingServices(false);
    }
  };

  const testCloudflareServices = async () => {
    setIsTestingServices(true);
    showInfo('Testing Services', 'Checking Cloudflare R2 and Worker connectivity...');
    
    try {
      // Test R2
      const r2Test = await testCloudflareR2();
      if (r2Test.success) {
        showSuccess('R2 Test Passed', r2Test.message);
      } else {
        showError('R2 Test Failed', r2Test.message);
      }
      
      // Test Worker
      const workerTest = await testCloudflareWorker();
      if (workerTest.success) {
        showSuccess('Worker Test Passed', workerTest.message);
      } else {
        showError('Worker Test Failed', workerTest.message);
      }
      
      // Refresh health status
      await checkProductionServices();
      
    } catch (error) {
      showError('Test Failed', 'Unable to test Cloudflare services');
    } finally {
      setIsTestingServices(false);
    }
  };

  const handleSaveCredentials = async () => {
    if (!apiCredentials.userId.trim() || !apiCredentials.apiKey.trim()) {
      showError('Validation Error', 'Both User ID and API Key are required');
      return;
    }

    setIsSavingCredentials(true);
    
    try {
      const success = updateHTMLCSStoImageCredentials(apiCredentials.userId, apiCredentials.apiKey);
      
      if (success) {
        showSuccess('Credentials Updated', 'HTMLCSStoImage API credentials have been saved successfully');
        setIsEditingCredentials(false);
        
        // Refresh health status to test new credentials
        await checkProductionServices();
      } else {
        showError('Save Failed', 'Failed to save API credentials');
      }
    } catch (error) {
      showError('Save Failed', 'An error occurred while saving credentials');
    } finally {
      setIsSavingCredentials(false);
    }
  };

  const handleResetCredentials = () => {
    resetHTMLCSStoImageCredentials();
    loadApiCredentials();
    setIsEditingCredentials(false);
    showInfo('Credentials Reset', 'API credentials have been reset to default values');
  };

  const handleTestApiCredentials = async () => {
    setIsTestingApi(true);
    showInfo('Testing API', 'Testing HTMLCSStoImage API with current credentials...');
    
    try {
      const result = await testHTMLCSStoImageAPI();
      
      if (result.success) {
        showSuccess('API Test Passed', 'HTMLCSStoImage API is working correctly with current credentials');
      } else {
        showError('API Test Failed', result.error || 'API test failed');
      }
    } catch (error) {
      showError('API Test Failed', 'Unable to test API credentials');
    } finally {
      setIsTestingApi(false);
    }
  };

  const handleCreateTestFile = () => {
    createTestHTMLFile('client1', 'general', '10801080', 1);
    showSuccess('Test File Created', 'HTML file downloaded. Upload it to your R2 bucket at: client1/general/10801080-1.html');
  };

  const handleCreateBackup = async () => {
    if (!user) return;

    setIsCreatingBackup(true);
    try {
      showInfo('Backup Started', 'Creating full system backup...');
      
      await createBackup(clients, users, user, {
        includeUsers: true,
        includeClients: true,
        includeBannerSets: true,
        includeAssets: true,
        compression: 'high',
        encryption: false
      });

      setLastBackup(new Date());
      localStorage.setItem('lastBackupTime', new Date().toISOString());
      
      showSuccess('Backup Complete', 'Full system backup has been created and downloaded');
    } catch (error) {
      showError('Backup Failed', 'Failed to create system backup. Please try again.');
    } finally {
      setIsCreatingBackup(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showSuccess('Copied', 'Text copied to clipboard');
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pass':
      case 'healthy':
      case 'up':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'warn':
      case 'warning':
      case 'degraded':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'fail':
      case 'critical':
      case 'down':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pass':
      case 'healthy':
      case 'up':
        return 'bg-green-50 border-green-200';
      case 'warn':
      case 'warning':
      case 'degraded':
        return 'bg-yellow-50 border-yellow-200';
      case 'fail':
      case 'critical':
      case 'down':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const formatLastBackup = () => {
    if (!lastBackup) return 'Never';
    
    const now = new Date();
    const diff = now.getTime() - lastBackup.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ago`;
    } else {
      return `${minutes}m ago`;
    }
  };

  const handleSaveCloudflareCredentials = async () => {
    if (!cloudflareCredentials.accountEmail.trim() || !cloudflareCredentials.apiKey.trim() || !cloudflareCredentials.zoneId.trim() || !cloudflareCredentials.accountId.trim()) {
      showError('Validation Error', 'Account Email, API Key, Zone ID, and Account ID are required');
      return;
    }

    setIsSavingCloudflare(true);
    
    try {
      const success = updateCloudflareCredentials({
        accountEmail: cloudflareCredentials.accountEmail.trim(),
        apiKey: cloudflareCredentials.apiKey.trim(),
        zoneId: cloudflareCredentials.zoneId.trim(),
        r2BucketName: cloudflareCredentials.r2BucketName.trim(),
        accountId: cloudflareCredentials.accountId.trim()
      });
      
      if (success) {
        showSuccess('Credentials Updated', 'Cloudflare credentials have been saved successfully');
        setIsEditingCloudflare(false);
        
        // Refresh health status to test new credentials
        await checkProductionServices();
      } else {
        showError('Save Failed', 'Failed to save Cloudflare credentials');
      }
    } catch (error) {
      showError('Save Failed', 'An error occurred while saving credentials');
    } finally {
      setIsSavingCloudflare(false);
    }
  };

  if (!hasPermission('settings', 'read')) {
    return (
      <div className="p-6 text-center">
        <Shield className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
        <p className="text-gray-600">You don't have permission to view system health.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setCurrentTab('health')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              currentTab === 'health'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Activity size={16} />
              <span>System Health</span>
            </div>
          </button>
          {hasPermission('settings', 'read') && (
            <>
              <button
                onClick={() => setCurrentTab('setup')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  currentTab === 'setup'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Settings size={16} />
                  <span>Production Setup</span>
                </div>
              </button>
              <button
                onClick={() => setCurrentTab('api')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  currentTab === 'api'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Key size={16} />
                  <span>API Settings</span>
                </div>
              </button>
            </>
          )}
        </nav>
      </div>

      {currentTab === 'health' ? (
        <>
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">System Health & Backups</h2>
              <p className="text-gray-600 mt-1">Monitor system status and manage data backups</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  performHealthCheck();
                  checkProductionServices();
                }}
                className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <RefreshCw size={16} />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* Overall System Status */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className={`p-6 rounded-lg border ${getStatusColor(healthStatus?.status || 'unknown')}`}>
              <div className="flex items-center space-x-3">
                {getStatusIcon(healthStatus?.status || 'unknown')}
                <div>
                  <h3 className="font-semibold text-gray-900">System Status</h3>
                  <p className="text-sm text-gray-600 capitalize">
                    {healthStatus?.status || 'Checking...'}
                  </p>
                </div>
              </div>
            </div>

            <div className={`p-6 rounded-lg border ${getStatusColor(integrityCheck?.isValid ? 'pass' : 'fail')}`}>
              <div className="flex items-center space-x-3">
                {getStatusIcon(integrityCheck?.isValid ? 'pass' : 'fail')}
                <div>
                  <h3 className="font-semibold text-gray-900">Data Integrity</h3>
                  <p className="text-sm text-gray-600">
                    {integrityCheck?.isValid ? 'All checks passed' : `${integrityCheck?.issues?.length || 0} issues found`}
                  </p>
                </div>
              </div>
            </div>

            <div className={`p-6 rounded-lg border ${getStatusColor(productionHealth?.status || 'unknown')}`}>
              <div className="flex items-center space-x-3">
                {getStatusIcon(productionHealth?.status || 'unknown')}
                <div>
                  <h3 className="font-semibold text-gray-900">Production Services</h3>
                  <p className="text-sm text-gray-600 capitalize">
                    {productionHealth?.status || 'Checking...'}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 rounded-lg border bg-blue-50 border-blue-200">
              <div className="flex items-center space-x-3">
                <Database className="w-5 h-5 text-blue-500" />
                <div>
                  <h3 className="font-semibold text-gray-900">Last Backup</h3>
                  <p className="text-sm text-gray-600">{formatLastBackup()}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Required Setup Steps */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Required Setup Steps</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-medium text-green-900">1. HTMLCSStoImage.com API</h3>
                  <p className="text-sm text-green-700 mt-1">✓ Configured and ready for image generation</p>
                  <div className="text-xs text-green-600 mt-2">
                    <div>User ID: {apiCredentials.userId}</div>
                    <div>API Key: {apiCredentials.apiKey ? '••••••••••••••••' : 'Not set'}</div>
                  </div>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-medium text-green-900">2. Domain Configuration</h3>
                  <p className="text-sm text-green-700 mt-1">✓ dealercentive.us configured in Netlify</p>
                  <div className="text-xs text-green-600 mt-2">
                    <div>Primary: dealercentive.us</div>
                    <div>Banners: banners.dealercentive.us</div>
                    <div>Images: images.dealercentive.us</div>
                  </div>
                </div>
              </div>

              <div className={`flex items-start space-x-4 p-4 rounded-lg border ${
                productionHealth?.services?.find((s: any) => s.name === 'Cloudflare R2')?.status === 'operational'
                  ? 'bg-green-50 border-green-200'
                  : 'bg-yellow-50 border-yellow-200'
              }`}>
                {productionHealth?.services?.find((s: any) => s.name === 'Cloudflare R2')?.status === 'operational' ? (
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5" />
                )}
                <div className="flex-1">
                  <h3 className={`font-medium ${
                    productionHealth?.services?.find((s: any) => s.name === 'Cloudflare R2')?.status === 'operational'
                      ? 'text-green-900'
                      : 'text-yellow-900'
                  }`}>
                    3. Cloudflare R2 Storage
                  </h3>
                  <p className={`text-sm mt-1 ${
                    productionHealth?.services?.find((s: any) => s.name === 'Cloudflare R2')?.status === 'operational'
                      ? 'text-green-700'
                      : 'text-yellow-700'
                  }`}>
                    {productionHealth?.services?.find((s: any) => s.name === 'Cloudflare R2')?.status === 'operational'
                      ? '✓ R2 bucket accessible at banners.dealercentive.us'
                      : '⏳ Setup required for HTML banner storage'
                    }
                  </p>
                  <div className={`text-xs mt-2 ${
                    productionHealth?.services?.find((s: any) => s.name === 'Cloudflare R2')?.status === 'operational'
                      ? 'text-green-600'
                      : 'text-yellow-600'
                  }`}>
                    <div>Account: support@dealercentive.com</div>
                    <div>Bucket: dealercentive-banners</div>
                    <div>Domain: banners.dealercentive.us</div>
                  </div>
                </div>
              </div>

              <div className={`flex items-start space-x-4 p-4 rounded-lg border ${
                productionHealth?.services?.find((s: any) => s.name === 'Cloudflare Worker')?.status === 'operational'
                  ? 'bg-green-50 border-green-200'
                  : 'bg-yellow-50 border-yellow-200'
              }`}>
                {productionHealth?.services?.find((s: any) => s.name === 'Cloudflare Worker')?.status === 'operational' ? (
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5" />
                )}
                <div className="flex-1">
                  <h3 className={`font-medium ${
                    productionHealth?.services?.find((s: any) => s.name === 'Cloudflare Worker')?.status === 'operational'
                      ? 'text-green-900'
                      : 'text-yellow-900'
                  }`}>
                    4. Cloudflare Worker
                  </h3>
                  <p className={`text-sm mt-1 ${
                    productionHealth?.services?.find((s: any) => s.name === 'Cloudflare Worker')?.status === 'operational'
                      ? 'text-green-700'
                      : 'text-yellow-700'
                  }`}>
                    {productionHealth?.services?.find((s: any) => s.name === 'Cloudflare Worker')?.status === 'operational'
                      ? '✓ Worker accessible at images.dealercentive.us'
                      : '⏳ Deploy auto-refresh worker for images'
                    }
                  </p>
                  <div className={`text-xs mt-2 ${
                    productionHealth?.services?.find((s: any) => s.name === 'Cloudflare Worker')?.status === 'operational'
                      ? 'text-green-600'
                      : 'text-yellow-600'
                  }`}>
                    <div>Worker: dealercentive-image-refresh</div>
                    <div>Route: images.dealercentive.us/*</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Backup Management */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Backup Management</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Manual Backup</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Create a complete backup of all system data including clients, users, banner sets, and assets.
                </p>
                <button
                  onClick={handleCreateBackup}
                  disabled={isCreatingBackup}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {isCreatingBackup ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      <span>Creating Backup...</span>
                    </>
                  ) : (
                    <>
                      <Download size={16} />
                      <span>Create Backup Now</span>
                    </>
                  )}
                </button>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-3">System Statistics</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{clients.length}</div>
                    <div className="text-sm text-gray-600">Total Clients</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{users.length}</div>
                    <div className="text-sm text-gray-600">Total Users</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">
                      {clients.reduce((total, client) => total + client.bannerSets.length, 0)}
                    </div>
                    <div className="text-sm text-gray-600">Banner Sets</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {clients.reduce((total, client) => 
                        total + client.bannerSets.reduce((setTotal: number, set: any) => 
                          setTotal + set.sizes.reduce((sizeTotal: number, size: any) => 
                            sizeTotal + size.elements.length, 0), 0), 0)}
                    </div>
                    <div className="text-sm text-gray-600">Total Elements</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : currentTab === 'api' ? (
        // API Settings Tab
        <div className="space-y-6">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">API Configuration</h1>
            <p className="text-lg text-gray-600">
              Manage HTMLCSStoImage.com API credentials for banner image generation
            </p>
          </div>

          {/* Current API Status */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">HTMLCSStoImage API Status</h2>
              <button
                onClick={handleTestApiCredentials}
                disabled={isTestingApi}
                className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {isTestingApi ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    <span>Testing...</span>
                  </>
                ) : (
                  <>
                    <TestTube size={16} />
                    <span>Test API</span>
                  </>
                )}
              </button>
            </div>

            {productionHealth && (
              <div className="mb-6">
                {productionHealth.services
                  .filter((service: any) => service.name.includes('HTMLCSStoImage'))
                  .map((service: any, index: number) => (
                    <div key={index} className={`p-4 rounded-lg border ${
                      service.status === 'operational' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                    }`}>
                      <div className="flex items-center space-x-2 mb-2">
                        {service.status === 'operational' ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <AlertTriangle className="w-5 h-5 text-red-500" />
                        )}
                        <span className="font-medium text-gray-900">{service.name}</span>
                      </div>
                      {service.responseTime && (
                        <p className="text-sm text-gray-600">Response time: {service.responseTime}ms</p>
                      )}
                      {service.error && (
                        <p className="text-sm text-red-600">{service.error}</p>
                      )}
                    </div>
                  ))}
              </div>
            )}

            {/* API Credentials Form */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">API Credentials</h3>
                <div className="flex items-center space-x-2">
                  {!isEditingCredentials ? (
                    <button
                      onClick={() => setIsEditingCredentials(true)}
                      className="flex items-center space-x-2 px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Settings size={16} />
                      <span>Edit</span>
                    </button>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setIsEditingCredentials(false);
                          loadApiCredentials(); // Reset to current values
                        }}
                        className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleResetCredentials}
                        className="flex items-center space-x-2 px-3 py-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                      >
                        <RotateCcw size={16} />
                        <span>Reset</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    User ID
                  </label>
                  <input
                    type="text"
                    value={apiCredentials.userId}
                    onChange={(e) => setApiCredentials(prev => ({ ...prev, userId: e.target.value }))}
                    disabled={!isEditingCredentials}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                    placeholder="Enter HTMLCSStoImage User ID"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Key
                  </label>
                  <div className="relative">
                    <input
                      type={showApiKey ? 'text' : 'password'}
                      value={apiCredentials.apiKey}
                      onChange={(e) => setApiCredentials(prev => ({ ...prev, apiKey: e.target.value }))}
                      disabled={!isEditingCredentials}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                      placeholder="Enter HTMLCSStoImage API Key"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              </div>

              {isEditingCredentials && (
                <div className="flex items-center space-x-3 pt-4">
                  <button
                    onClick={handleSaveCredentials}
                    disabled={isSavingCredentials || !apiCredentials.userId.trim() || !apiCredentials.apiKey.trim()}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {isSavingCredentials ? (
                      <>
                        <RefreshCw size={16} className="animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        <span>Save Credentials</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* API Information */}
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">About HTMLCSStoImage.com</h4>
              <div className="text-sm text-blue-800 space-y-2">
                <p>HTMLCSStoImage.com is used to generate high-quality images from HTML banners for auto-refreshing deployment.</p>
                <div className="space-y-1">
                  <p><strong>Features:</strong></p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li>High-resolution image generation (up to 2x scale)</li>
                    <li>Support for custom fonts and CSS</li>
                    <li>PNG and JPEG output formats</li>
                    <li>Fast API response times</li>
                  </ul>
                </div>
                <div className="mt-3 pt-3 border-t border-blue-300">
                  <p><strong>Getting API Credentials:</strong></p>
                  <ol className="list-decimal list-inside ml-4 space-y-1">
                    <li>Sign up at <a href="https://htmlcsstoimage.com" target="_blank" rel="noopener noreferrer" className="underline">htmlcsstoimage.com</a></li>
                    <li>Go to your dashboard and find your User ID and API Key</li>
                    <li>Enter them in the form above and save</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Production Setup Tab
        <div className="space-y-6">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Production Setup Guide</h1>
            <p className="text-lg text-gray-600">
              Configure your production infrastructure for live banner deployment
            </p>
          </div>

          {/* Current Status */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Current Status</h2>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleCreateTestFile}
                  className="flex items-center space-x-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <FileText size={16} />
                  <span>Create Test File</span>
                </button>
                <button
                  onClick={checkProductionServices}
                  className="flex items-center space-x-2 px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <RefreshCw size={16} />
                  <span>Check Status</span>
                </button>
              </div>
            </div>

            {productionHealth && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {productionHealth.services.map((service: any, index: number) => (
                  <div key={index} className={`p-4 rounded-lg border ${
                    service.status === 'operational' ? 'bg-green-50 border-green-200' : 
                    service.status === 'unknown' ? 'bg-gray-50 border-gray-200' :
                    'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-center space-x-2 mb-2">
                      {service.status === 'operational' ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : service.status === 'unknown' ? (
                        <Clock className="w-5 h-5 text-gray-500" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                      )}
                      <span className="font-medium text-gray-900">{service.name}</span>
                    </div>
                    {service.responseTime && (
                      <p className="text-sm text-gray-600">Response: {service.responseTime}ms</p>
                    )}
                    {service.error && (
                      <p className="text-sm text-red-600">{service.error}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Setup Steps */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Required Setup Steps</h2>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-medium text-green-900">1. HTMLCSStoImage.com API</h3>
                  <p className="text-sm text-green-700 mt-1">✓ Configured and ready for image generation</p>
                  <div className="text-xs text-green-600 mt-2">
                    <div>User ID: {apiCredentials.userId}</div>
                    <div>API Key: {apiCredentials.apiKey ? '••••••••••••••••' : 'Not set'}</div>
                  </div>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-medium text-green-900">2. Domain Configuration</h3>
                  <p className="text-sm text-green-700 mt-1">✓ dealercentive.us configured in Netlify</p>
                  <div className="text-xs text-green-600 mt-2">
                    <div>Primary: dealercentive.us</div>
                    <div>Banners: banners.dealercentive.us</div>
                    <div>Images: images.dealercentive.us</div>
                  </div>
                </div>
              </div>

              <div className={`flex items-start space-x-4 p-4 rounded-lg border ${
                productionHealth?.services?.find((s: any) => s.name === 'Cloudflare R2')?.status === 'operational'
                  ? 'bg-green-50 border-green-200'
                  : 'bg-yellow-50 border-yellow-200'
              }`}>
                {productionHealth?.services?.find((s: any) => s.name === 'Cloudflare R2')?.status === 'operational' ? (
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5" />
                )}
                <div className="flex-1">
                  <h3 className={`font-medium ${
                    productionHealth?.services?.find((s: any) => s.name === 'Cloudflare R2')?.status === 'operational'
                      ? 'text-green-900'
                      : 'text-yellow-900'
                  }`}>
                    3. Cloudflare R2 Storage
                  </h3>
                  <p className={`text-sm mt-1 ${
                    productionHealth?.services?.find((s: any) => s.name === 'Cloudflare R2')?.status === 'operational'
                      ? 'text-green-700'
                      : 'text-yellow-700'
                  }`}>
                    {productionHealth?.services?.find((s: any) => s.name === 'Cloudflare R2')?.status === 'operational'
                      ? '✓ R2 bucket accessible at banners.dealercentive.us'
                      : '⏳ Setup required for HTML banner storage'
                    }
                  </p>
                  <div className={`text-xs mt-2 ${
                    productionHealth?.services?.find((s: any) => s.name === 'Cloudflare R2')?.status === 'operational'
                      ? 'text-green-600'
                      : 'text-yellow-600'
                  }`}>
                    <div>Account: support@dealercentive.com</div>
                    <div>Bucket: dealercentive-banners</div>
                    <div>Domain: banners.dealercentive.us</div>
                  </div>
                </div>
              </div>

              <div className={`flex items-start space-x-4 p-4 rounded-lg border ${
                productionHealth?.services?.find((s: any) => s.name === 'Cloudflare Worker')?.status === 'operational'
                  ? 'bg-green-50 border-green-200'
                  : 'bg-yellow-50 border-yellow-200'
              }`}>
                {productionHealth?.services?.find((s: any) => s.name === 'Cloudflare Worker')?.status === 'operational' ? (
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5" />
                )}
                <div className="flex-1">
                  <h3 className={`font-medium ${
                    productionHealth?.services?.find((s: any) => s.name === 'Cloudflare Worker')?.status === 'operational'
                      ? 'text-green-900'
                      : 'text-yellow-900'
                  }`}>
                    4. Cloudflare Worker
                  </h3>
                  <p className={`text-sm mt-1 ${
                    productionHealth?.services?.find((s: any) => s.name === 'Cloudflare Worker')?.status === 'operational'
                      ? 'text-green-700'
                      : 'text-yellow-700'
                  }`}>
                    {productionHealth?.services?.find((s: any) => s.name === 'Cloudflare Worker')?.status === 'operational'
                      ? '✓ Worker accessible at images.dealercentive.us'
                      : '⏳ Deploy auto-refresh worker for images'
                    }
                  </p>
                  <div className={`text-xs mt-2 ${
                    productionHealth?.services?.find((s: any) => s.name === 'Cloudflare Worker')?.status === 'operational'
                      ? 'text-green-600'
                      : 'text-yellow-600'
                  }`}>
                    <div>Worker: dealercentive-image-refresh</div>
                    <div>Route: images.dealercentive.us/*</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Manual Upload Instructions */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-medium text-yellow-900 mb-2 flex items-center space-x-2">
              <FileText className="w-5 h-5" />
              <span>Test the Auto-Refresh System</span>
            </h4>
            <div className="text-sm text-yellow-800 space-y-2">
              <p>To verify the system is working correctly:</p>
              <ol className="list-decimal list-inside ml-4 space-y-1">
                <li>Click the <strong>"Create Test File"</strong> button above to download a test HTML file</li>
                <li>Go to your Cloudflare R2 bucket: <code>dealercentive-banners</code></li>
                <li>Create folders: <code>client1/general/</code></li>
                <li>Upload the file as: <code>10801080-1.html</code></li>
                <li>Test the URL: <code>https://images.dealercentive.us/client1/general/10801080-1.png</code></li>
              </ol>
              <p className="mt-2">If everything is configured correctly, you'll see a generated image from your HTML file.</p>
            </div>
          </div>

          {/* Quick Setup Commands */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Setup Commands</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-gray-900 mb-2">1. Create Cloudflare R2 Bucket</h3>
                <div className="bg-gray-50 border border-gray-200 rounded p-3 flex items-center justify-between">
                  <code className="text-sm">wrangler r2 bucket create dealercentive-banners</code>
                  <button
                    onClick={() => copyToClipboard('wrangler r2 bucket create dealercentive-banners')}
                    className="ml-2 text-blue-600 hover:text-blue-800 p-1"
                  >
                    <Copy size={14} />
                  </button>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-2">2. Deploy Cloudflare Worker</h3>
                <div className="bg-gray-50 border border-gray-200 rounded p-3 flex items-center justify-between">
                  <code className="text-sm">wrangler deploy --name dealercentive-image-refresh</code>
                  <button
                    onClick={() => copyToClipboard('wrangler deploy --name dealercentive-image-refresh')}
                    className="ml-2 text-blue-600 hover:text-blue-800 p-1"
                  >
                    <Copy size={14} />
                  </button>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-2">3. View Setup Guide</h3>
                <div className="bg-gray-50 border border-gray-200 rounded p-3">
                  <p className="text-sm text-gray-600 mb-2">
                    Complete setup instructions are available in the project root:
                  </p>
                  <div className="flex items-center space-x-2">
                    <code className="text-sm bg-white px-2 py-1 rounded border">CLOUDFLARE_SETUP_GUIDE.md</code>
                    <ExternalLink size={14} className="text-gray-500" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-blue-900 mb-4">Next Steps</h2>
            <div className="space-y-3 text-blue-800">
              <div className="flex items-start space-x-2">
                <CheckCircle size={16} className="mt-0.5 flex-shrink-0" />
                <span>Complete Cloudflare R2 bucket setup with custom domain</span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle size={16} className="mt-0.5 flex-shrink-0" />
                <span>Deploy the Cloudflare Worker script for auto-refresh functionality</span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle size={16} className="mt-0.5 flex-shrink-0" />
                <span>Test deployment with a sample banner to verify everything works</span>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle size={16} className="mt-0.5 flex-shrink-0" />
                <span>Set up monitoring and alerts for production deployment health</span>
              </div>
            </div>
            
            <div className="mt-4 p-4 bg-white rounded border border-blue-200">
              <p className="text-sm text-blue-700">
                <strong>Estimated Setup Time:</strong> 20-30 minutes total
              </p>
              <p className="text-sm text-blue-700 mt-1">
                <strong>Need Help?</strong> Contact support@dealercentive.com for assistance with Cloudflare setup.
              </p>
            </div>
          </div>

          {/* Cloudflare Credentials Section */}
          <div className="mt-6 bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Globe className="w-5 h-5 mr-2" />
                Cloudflare Configuration
              </h3>
            </div>
            
            <div className="p-4">
              {!isEditingCloudflare ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Account Email</label>
                      <p className="mt-1 text-sm text-gray-900">{cloudflareCredentials.accountEmail || 'Not configured'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Zone ID</label>
                      <p className="mt-1 text-sm text-gray-900">{cloudflareCredentials.zoneId || 'Not configured'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">API Key</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {cloudflareCredentials.apiKey ? '••••••••••••••••' : 'Not configured'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">R2 Bucket</label>
                      <p className="mt-1 text-sm text-gray-900">{cloudflareCredentials.r2BucketName}</p>
                    </div>
                  </div>
                  
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setIsEditingCloudflare(true)}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Edit Credentials
                    </button>
                    
                    <button
                      onClick={testCloudflareServices}
                      disabled={isTestingServices}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      <TestTube className="w-4 h-4 mr-2" />
                      Test Services
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Account Email</label>
                      <input
                        type="email"
                        value={cloudflareCredentials.accountEmail}
                        onChange={(e) => setCloudflareCredentials(prev => ({ ...prev, accountEmail: e.target.value }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="your-email@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Zone ID</label>
                      <input
                        type="text"
                        value={cloudflareCredentials.zoneId}
                        onChange={(e) => setCloudflareCredentials(prev => ({ ...prev, zoneId: e.target.value }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Your Cloudflare Zone ID"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">API Key</label>
                      <div className="mt-1 relative">
                        <input
                          type={showCloudflareKey ? 'text' : 'password'}
                          value={cloudflareCredentials.apiKey}
                          onChange={(e) => setCloudflareCredentials(prev => ({ ...prev, apiKey: e.target.value }))}
                          className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm pr-10"
                          placeholder="Your Cloudflare API Key"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCloudflareKey(!showCloudflareKey)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                          {showCloudflareKey ? (
                            <EyeOff className="h-4 w-4 text-gray-400" />
                          ) : (
                            <Eye className="h-4 w-4 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">R2 Bucket</label>
                      <input
                        type="text"
                        value={cloudflareCredentials.r2BucketName}
                        onChange={(e) => setCloudflareCredentials(prev => ({ ...prev, r2BucketName: e.target.value }))}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="dealercentive-banners"
                      />
                    </div>
                  </div>
                  
                  <div className="flex space-x-3">
                    <button
                      onClick={handleSaveCloudflareCredentials}
                      disabled={isSavingCloudflare}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save Credentials
                    </button>
                    
                    <button
                      onClick={() => {
                        setIsEditingCloudflare(false);
                        loadCloudflareCredentials();
                      }}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};